import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { invalidateCaseQueries } from "@/utils/cacheUtils.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { handleJsonApiError } from "jsonapi-zod-query";

type CostRowData = {
    caseUUID: string;
    costBill?: number | null;
    costInsurance?: number | null;
    costAdj?: number | null;
    costSelf?: number | null;
    costMedPay?: number | null;
    costCurrentBalance?: number | null;
    costReduction?: number | null;
    costSent?: string | null;
    costApproved?: string | null;
};

export const useSaveCostRowMutation = (): UseMutationResult<void, Error, CostRowData> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            caseUUID,
            costBill,
            costInsurance,
            costAdj,
            costSelf,
            costMedPay,
            costCurrentBalance,
            costReduction,
            costSent,
            costApproved,
        }) => {
            const response = await fetch(apiUrl(`/negotiation/save-cost/${caseUUID}`), {
                method: "POST",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    data: {
                        id: caseUUID,
                        type: "case",
                        attributes: {
                            costBill,
                            costInsurance,
                            costAdj,
                            costSelf,
                            costMedPay,
                            costCurrentBalance,
                            costReduction,
                            costSent,
                            costApproved,
                        },
                    },
                }),
            });
            await handleJsonApiError(response);
        },
        onSuccess: async (_data, { caseUUID }) => {
            await invalidateCaseQueries(queryClient, caseUUID);
            await queryClient.invalidateQueries({
                queryKey: ["disbursement", caseUUID],
            });
        },
    });
};
